import { useEffect, useMemo, useRef } from 'react';
import { AsyncSectionNavigator, pushUrl, readQueryParam } from '../../../ips-react-front-fw/navigation';
import { doneTopProgress, failTopProgress, startTopProgress } from '../../../ips-react-front-fw/progress';
import { getAccountFrontData } from '../frontData';
import { fetchMembersAreaSection } from './api';

const sectionSelector = '.ips-membership-section-content';
const pageLinkSelector = 'a.wc-memberships-members-area-page-link';
const loadingClass = 'ips-membership-pagination-loading';
const pageQueryParam = 'members_area_section_page';

type MembershipFrontConfig = {
  endpoint: string;
  nonce: string;
  endpointSlug: string;
};

const readFrontConfig = (): MembershipFrontConfig => {
  const frontData = getAccountFrontData();
  const membershipData = frontData.membership || {};

  const fallbackRestUrl = typeof frontData.restUrl === 'string' ? frontData.restUrl : '/wp-json/';
  const fallbackEndpoint = `${String(fallbackRestUrl).replace(/\/+$/, '')}/ips-order-account/v1/membership-members-area-section`;

  return {
    endpoint: membershipData.sectionEndpoint || fallbackEndpoint,
    nonce: typeof frontData.restNonce === 'string' ? frontData.restNonce : '',
    endpointSlug: membershipData.endpointSlug || 'members-area',
  };
};

const parsePageFromHref = (href: string): number => {
  const pageFromQuery = readQueryParam(href, pageQueryParam);
  if (pageFromQuery && /^\d+$/.test(pageFromQuery)) {
    return Math.max(1, Number.parseInt(pageFromQuery, 10));
  }

  try {
    const url = new URL(href, window.location.origin);
    const segments = url.pathname.split('/').filter(Boolean);

    for (let i = 0; i < segments.length; i += 1) {
      const segment = segments[i];
      if (!segment || !segment.startsWith('my-membership-')) {
        continue;
      }

      const possiblePage = segments[i + 1];
      if (possiblePage && /^\d+$/.test(possiblePage)) {
        return Math.max(1, Number.parseInt(possiblePage, 10));
      }
    }
  } catch (error) {
    return 1;
  }

  return 1;
};

const parseSortingFromHref = (href: string): { sortBy?: string; sortOrder?: string } => {
  const sortByRaw = readQueryParam(href, 'sort_by');
  const sortOrderRaw = readQueryParam(href, 'sort_order');

  const sortBy =
    sortByRaw && (sortByRaw === 'title' || sortByRaw === 'type' || sortByRaw === 'post_type')
      ? sortByRaw
      : undefined;
  const sortOrder = sortOrderRaw && /^(asc|desc)$/i.test(sortOrderRaw) ? sortOrderRaw.toUpperCase() : undefined;

  return { sortBy, sortOrder };
};

const getSectionContainer = (element: Element | null): HTMLElement | null => {
  if (!element) {
    return null;
  }

  return element.closest<HTMLElement>(sectionSelector);
};

const readSectionMeta = (container: HTMLElement): { planId: number; section: string } | null => {
  const planId = Number.parseInt(container.dataset.ipsMembershipPlanId || '', 10);
  const section = (container.dataset.ipsMembershipSection || '').trim();

  if (!Number.isFinite(planId) || planId < 1 || !section) {
    return null;
  }

  return { planId, section };
};

export const useMembersAreaPagination = (): void => {
  const config = useMemo(() => readFrontConfig(), []);
  const navigatorRef = useRef<AsyncSectionNavigator | null>(null);

  if (!navigatorRef.current) {
    navigatorRef.current = new AsyncSectionNavigator();
  }

  useEffect(() => {
    if (!config.endpoint || !config.nonce) {
      return;
    }

    const navigator = navigatorRef.current;
    if (!navigator) {
      return;
    }

    const loadSection = async (targetHref: string, container: HTMLElement, syncHistory: boolean): Promise<void> => {
      const meta = readSectionMeta(container);
      if (!meta) {
        if (syncHistory) {
          window.location.href = targetHref;
        }
        return;
      }

      const page = parsePageFromHref(targetHref);
      const { sortBy, sortOrder } = parseSortingFromHref(targetHref);

      startTopProgress();
      container.classList.add(loadingClass);
      let hasError = false;

      try {
        const payload = await navigator.run((signal) =>
          fetchMembersAreaSection(
            {
              endpoint: config.endpoint,
              nonce: config.nonce,
              planId: meta.planId,
              section: meta.section,
              page,
              sortBy,
              sortOrder,
            },
            signal
          )
        );

        container.outerHTML = payload.html;

        if (syncHistory) {
          pushUrl(targetHref, {
            ipsMembershipPagination: true,
          });
        }
      } catch (error: any) {
        if (error?.name === 'AbortError') {
          return;
        }

        hasError = true;
        if (syncHistory) {
          window.location.href = targetHref;
        }
      } finally {
        if (hasError) {
          failTopProgress();
        } else {
          doneTopProgress();
        }

        const refreshedContainer = document.querySelector<HTMLElement>(sectionSelector);
        if (refreshedContainer) {
          refreshedContainer.classList.remove(loadingClass);
        }
      }
    };

    const onClick = (event: Event) => {
      const target = event.target as HTMLElement | null;
      const anchor = target?.closest<HTMLAnchorElement>(pageLinkSelector);
      if (!anchor) {
        return;
      }

      const container = getSectionContainer(anchor);
      if (!container) {
        return;
      }

      event.preventDefault();
      void loadSection(anchor.href, container, true);
    };

    const onPopState = () => {
      const container = document.querySelector<HTMLElement>(sectionSelector);
      if (!container) {
        return;
      }

      void loadSection(window.location.href, container, false);
    };

    document.addEventListener('click', onClick, true);
    window.addEventListener('popstate', onPopState);

    return () => {
      document.removeEventListener('click', onClick, true);
      window.removeEventListener('popstate', onPopState);
      navigator.abort();
    };
  }, [config.endpoint, config.nonce, config.endpointSlug]);
};
